import React, { useState, useEffect, useMemo, useCallback } from "react";
import { TextInputField, SelectField, InlinePricingRow, PrimaryButton } from "./components";
import { useToast } from "../../../../hooks/useToast";
import { useAuthStore } from "../../../../hooks/useAuthStore";
import providersService from "../../../../api/services/providersService";

const defaultPricingForm = {
  tenHourRate: "",
  securityService: "",
  miceShuttlePrice: "",
  pickUpPrice: "",
  anyOtherRate: "",
  sedan: "",
  suv: "",
  van: "",
  bus: "",
  bank: "",
  accountNumber: "",
  accountName: "",
};

const primaryRateField = { key: "tenHourRate", label: "12-Hour Rate" };

const securityPricingMeta = [
  { key: "securityService", label: "MOPOL (Mobile Police)" },
  { key: "miceShuttlePrice", label: "Police Officer" },
  { key: "pickUpPrice", label: "Army Officer" },
];

const vehiclePricingMeta = [
  { key: "sedan", label: "Sedan" },
  { key: "suv", label: "SUV" },
  { key: "bus", label: "Truck" },
];

const fallbackBanks = [
  { name: "Access Bank", code: "044", currency: "NGN" },
  { name: "GTBank", code: "058", currency: "NGN" },
  { name: "Zenith Bank", code: "057", currency: "NGN" },
  { name: "UBA", code: "033", currency: "NGN" },
  { name: "First Bank", code: "011", currency: "NGN" },
  { name: "Sterling Bank", code: "232", currency: "NGN" },
  { name: "Kuda", code: "50211", currency: "NGN" },
  { name: "Opay", code: "999992", currency: "NGN" },
];

const parsePositiveNumber = (value) => {
  const cleaned = String(value ?? "").replace(/[^\d.]/g, "");
  if (!cleaned) return undefined;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const unwrapProfile = (response) => {
  if (!response) return {};
  return (
    response.data?.data?.profile ||
    response.data?.data?.user ||
    response.data?.data ||
    response.data?.profile ||
    response.data ||
    response.profile ||
    response
  );
};

const extractPayment = (paymentResponse, profileResponse = {}) => {
  const payment = unwrapProfile(paymentResponse);
  if (payment && Object.keys(payment).length) {
    return (
      payment.payment ||
      payment.pricingAndAccount ||
      payment.pricing ||
      payment.bankAccount ||
      payment.accountDetails ||
      payment.bank ||
      payment
    );
  }

  return (
    profileResponse.payment ||
    profileResponse.pricingAndAccount ||
    profileResponse.pricing ||
    profileResponse.bankAccount ||
    profileResponse.accountDetails ||
    profileResponse.bank ||
    {}
  );
};

export default function PricingPanel({ profile, onRequestReview }) {
  const toast = useToast();
  const { user } = useAuthStore();

  const storedUserKey = useMemo(() => {
    if (typeof window === "undefined") return "guest";
    if (!user) return "guest";
    return user?.id || user?._id || user?.email || "guest";
  }, [user]);

  const pricingDraftKey = useMemo(
    () => `assistantSettingsPricingDraft_${storedUserKey}`,
    [storedUserKey]
  );

  const [pricingForm, setPricingForm] = useState(defaultPricingForm);
  const [banks, setBanks] = useState([]);
  const [bankNotice, setBankNotice] = useState("");
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);
  const [isSavingPricing, setIsSavingPricing] = useState(false);
  const [isResolvingAccount, setIsResolvingAccount] = useState(false);

  const normalizeBankName = useCallback(
    (value) => {
      if (!value) return "";
      const raw = String(value).trim();
      const match = banks.find(
        (item) => String(item?.name || "").toLowerCase() === raw.toLowerCase()
      );
      return match?.name || raw;
    },
    [banks]
  );

  // Fetch Banks
  useEffect(() => {
    let active = true;
    const fetchBanks = async () => {
      setIsLoadingBanks(true);
      try {
        const response = await providersService.getBanks();
        const list = response?.banks || response?.data?.banks || [];
        if (!active) return;
        if (list.length) {
          setBanks(list);
          setBankNotice("");
        } else {
          setBanks(fallbackBanks);
          setBankNotice("Bank list is temporarily unavailable. Showing a fallback list.");
        }
      } catch (err) {
        if (!active) return;
        setBanks(fallbackBanks);
        setBankNotice("Bank list is temporarily unavailable. Showing a fallback list.");
      } finally {
        if (active) setIsLoadingBanks(false);
      }
    };

    fetchBanks();
    return () => {
      active = false;
    };
  }, []);

  // Sync pricing data from profile & local storage draft
  useEffect(() => {
    if (profile) {
      const paymentData = extractPayment(profile, profile);
      const pricingDraft = (() => {
        if (typeof window === "undefined") return {};
        try {
          const raw = localStorage.getItem(pricingDraftKey);
          return raw ? JSON.parse(raw) : {};
        } catch {
          return {};
        }
      })();

      setPricingForm({
        ...defaultPricingForm,
        ...pricingDraft,
        tenHourRate: String(
          paymentData.pricePerDay ??
            paymentData.price_per_day ??
            paymentData.price ??
            pricingDraft.tenHourRate ??
            ""
        ),
        securityService: String(
          paymentData.securityService ??
            paymentData.security_service ??
            pricingDraft.securityService ??
            ""
        ),
        miceShuttlePrice: String(
          paymentData.miceShuttlePrice ??
            paymentData.mice_shuttle_price ??
            pricingDraft.miceShuttlePrice ??
            ""
        ),
        pickUpPrice: String(
          paymentData.pickUpPrice ??
            paymentData.pick_up_price ??
            paymentData.pickupPrice ??
            pricingDraft.pickUpPrice ??
            ""
        ),
        anyOtherRate: String(
          paymentData.anyOtherRate ??
            paymentData.any_other_rate ??
            pricingDraft.anyOtherRate ??
            ""
        ),
        sedan: String(paymentData.sedan ?? pricingDraft.sedan ?? ""),
        suv: String(paymentData.suv ?? pricingDraft.suv ?? ""),
        van: String(paymentData.van ?? pricingDraft.van ?? ""),
        bus: String(paymentData.bus ?? pricingDraft.bus ?? ""),
        bank:
          paymentData.bankName ||
          paymentData.bank ||
          paymentData.bank_name ||
          pricingDraft.bank ||
          "",
        accountNumber:
          String(paymentData.accountNumber || pricingDraft.accountNumber || ""),
        accountName:
          paymentData.accountName || pricingDraft.accountName || "",
      });
    }
  }, [profile, pricingDraftKey]);

  // Normalize bank name when bank list loads
  useEffect(() => {
    if (!banks.length || !pricingForm.bank) return;
    const normalized = normalizeBankName(pricingForm.bank);
    if (normalized && normalized !== pricingForm.bank) {
      setPricingForm((prev) => ({ ...prev, bank: normalized }));
    }
  }, [banks, normalizeBankName, pricingForm.bank]);

  // Save draft to localStorage
  useEffect(() => {
    if (typeof window === "undefined" || !pricingDraftKey) return;
    localStorage.setItem(pricingDraftKey, JSON.stringify(pricingForm));
  }, [pricingForm, pricingDraftKey]);

  // Resolve account name
  useEffect(() => {
    let active = true;
    const selectedBank = banks.find((item) => item.name === pricingForm.bank) || {};
    const accountNumber = pricingForm.accountNumber.trim();

    if (!selectedBank.code || accountNumber.length < 10) {
      return;
    }

    const timer = setTimeout(async () => {
      setIsResolvingAccount(true);
      try {
        const response = await providersService.resolveBankAccount({
          accountNumber,
          bankCode: selectedBank.code,
        });
        const resolvedName = response?.accountName || response?.data?.accountName || "";
        if (active && resolvedName) {
          setPricingForm((prev) => ({ ...prev, accountName: resolvedName }));
        }
      } catch (error) {
        if (!active) return;
        toast.apiError(error, "Unable to resolve account name.");
      } finally {
        if (active) setIsResolvingAccount(false);
      }
    }, 500);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [banks, pricingForm.accountNumber, pricingForm.bank, toast]);

  const validatePricingForm = () => {
    const missingPricingField = [primaryRateField, ...securityPricingMeta, ...vehiclePricingMeta].find(
      (item) => !parsePositiveNumber(pricingForm[item.key])
    );

    if (missingPricingField) {
      toast.error(`Please enter ${missingPricingField.label.toLowerCase()}.`);
      return false;
    }
    if (!pricingForm.bank) {
      toast.error("Please select your withdrawal bank.");
      return false;
    }
    if (!pricingForm.accountNumber.trim()) {
      toast.error("Please enter your account number.");
      return false;
    }
    if (!pricingForm.accountName.trim()) {
      toast.error("Please enter your account name.");
      return false;
    }

    const selectedBank = banks.find((item) => item.name === pricingForm.bank);
    if (!selectedBank?.code) {
      toast.error("Please select a valid bank from the list.");
      return false;
    }

    return true;
  };

  const handleSaveClick = () => {
    if (!validatePricingForm()) return;
    const selectedBank = banks.find((item) => item.name === pricingForm.bank);

    onRequestReview("pricing", async () => {
      setIsSavingPricing(true);
      try {
        await providersService.updatePayment({
          pricePerDay: parsePositiveNumber(pricingForm.tenHourRate),
          accountNumber: pricingForm.accountNumber.trim(),
          accountName: pricingForm.accountName.trim(),
          bankCode: selectedBank.code,
          bankName: selectedBank.name,
          securityService: parsePositiveNumber(pricingForm.securityService),
          miceShuttlePrice: parsePositiveNumber(pricingForm.miceShuttlePrice),
          pickUpPrice: parsePositiveNumber(pricingForm.pickUpPrice),
          anyOtherRate: parsePositiveNumber(pricingForm.anyOtherRate),
          sedan: parsePositiveNumber(pricingForm.sedan),
          suv: parsePositiveNumber(pricingForm.suv),
          van: parsePositiveNumber(pricingForm.van),
          bus: parsePositiveNumber(pricingForm.bus),
        });
        toast.success("Pricing and account details updated successfully.");
        return true;
      } catch (error) {
        toast.apiError(error, "Unable to update pricing and account details.");
        return false;
      } finally {
        setIsSavingPricing(false);
      }
    });
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-8 xl:grid-cols-2">
        <div className="space-y-6">
          <TextInputField
            label={primaryRateField.label}
            value={pricingForm[primaryRateField.key]}
            onChange={(value) => {
              setPricingForm((prev) => ({
                ...prev,
                [primaryRateField.key]: value,
              }));
            }}
            placeholder="₦0.00"
            inputMode="decimal"
          />

          <div className="space-y-4">
            <h4 className="text-2xl font-semibold text-[#344054]">
              Security Service
            </h4>
            {securityPricingMeta.map((field) => (
              <InlinePricingRow
                key={field.key}
                label={field.label}
                value={pricingForm[field.key]}
                onChange={(value) => {
                  setPricingForm((prev) => ({ ...prev, [field.key]: value }));
                }}
                placeholder="₦0.00"
              />
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-4">
            <h4 className="text-2xl font-semibold text-[#344054]">Vehicles</h4>
            {vehiclePricingMeta.map((field) => (
              <InlinePricingRow
                key={field.key}
                label={field.label}
                value={pricingForm[field.key]}
                onChange={(value) => {
                  setPricingForm((prev) => ({ ...prev, [field.key]: value }));
                }}
                placeholder="₦0.00"
              />
            ))}
          </div>

          <div className="space-y-4">
            <h4 className="text-2xl font-semibold text-[#344054]">
              Set Withdrawal Bank
            </h4>
            <div className="mt-4 space-y-4">
              <SelectField
                label="Bank"
                value={pricingForm.bank}
                onChange={(value) => {
                  setPricingForm((prev) => ({ ...prev, bank: value }));
                }}
                options={banks.map((bank) => bank.name)}
                placeholder={isLoadingBanks ? "Loading banks..." : "Select Bank"}
              />
              <div className="space-y-2">
                <TextInputField
                  label="Account Number"
                  value={pricingForm.accountNumber}
                  onChange={(value) => {
                    setPricingForm((prev) => ({ ...prev, accountNumber: value }));
                  }}
                  placeholder="Enter account number"
                  inputMode="numeric"
                />
                <p className="min-h-[1.5rem] text-right text-base font-medium text-[#667085]">
                  {isResolvingAccount
                    ? "Resolving account name..."
                    : pricingForm.accountName || ""}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {bankNotice ? <p className="text-base font-medium text-[#667085]">{bankNotice}</p> : null}
      <div className="flex justify-end pt-4">
        <PrimaryButton
          onClick={handleSaveClick}
          disabled={isSavingPricing}
          label={isSavingPricing ? "Saving..." : "Save Changes"}
        />
      </div>
    </div>
  );
}
